import type { HTMLAttributes, ReactNode } from 'react';
import type {
  FailureKind,
  PlanStepType,
  RunStatus,
  RunTrigger,
  ScheduleStatus,
  StepStatus,
} from '@verifyloop/core';
import { formatDuration } from '../lib/format';

type Tone = 'neutral' | 'pass' | 'fail' | 'running' | 'pending' | 'warn' | 'info';

const RUN_TONES: Record<RunStatus, Tone> = {
  passed: 'pass',
  failed: 'fail',
  running: 'running',
  queued: 'pending',
  blocked: 'warn',
  cancelled: 'neutral',
  inconclusive: 'warn',
};

const STEP_TONES: Record<StepStatus, Tone> = {
  passed: 'pass',
  failed: 'fail',
  pending: 'pending',
  skipped: 'neutral',
  blocked: 'warn',
};

const FAILURE_KIND_TONES: Record<FailureKind, Tone> = {
  assertion: 'fail',
  navigation: 'fail',
  timeout: 'fail',
  'element-missing': 'fail',
  'runtime-error': 'fail',
  'network-error': 'fail',
  auth: 'warn',
  dependency: 'warn',
  infrastructure: 'warn',
  healed: 'info',
  flaky: 'warn',
};

const SCHEDULE_TONES: Record<ScheduleStatus, Tone> = {
  ENABLED: 'pass',
  PAUSED: 'neutral',
  AUTO_PAUSED: 'warn',
};

export function RunStatusPill({ status, ...rest }: { status: RunStatus } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return <Pill tone={RUN_TONES[status]} {...rest}>{status}</Pill>;
}

export function StepStatusPill({ status, ...rest }: { status: StepStatus } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return <Pill tone={STEP_TONES[status]} {...rest}>{status}</Pill>;
}

export function FailureKindPill({ kind, ...rest }: { kind: FailureKind | undefined } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  if (!kind) return <Pill tone="neutral" {...rest}>none</Pill>;
  return <Pill tone={FAILURE_KIND_TONES[kind] ?? 'neutral'} {...rest}>{kind}</Pill>;
}

export function ScheduleStatusPill({ status, ...rest }: { status: ScheduleStatus } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return <Pill tone={SCHEDULE_TONES[status] ?? 'neutral'} {...rest}>{status.replace('_', ' ').toLowerCase()}</Pill>;
}

export function StepTypePill({ type, ...rest }: { type: PlanStepType } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return <Pill tone="info" subtle {...rest}>{type}</Pill>;
}

export function RunTriggerTag({ trigger, ...rest }: { trigger: RunTrigger } & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return <Pill tone="neutral" subtle {...rest}>{trigger}</Pill>;
}

export function Pill({
  tone = 'neutral',
  subtle = false,
  children,
  ...rest
}: {
  tone?: Tone;
  subtle?: boolean;
  children: ReactNode;
} & HTMLAttributes<HTMLSpanElement>): JSX.Element {
  return (
    <span
      className={`pill pill--${tone}${subtle ? ' pill--subtle' : ''}`}
      {...rest}
    >
      {children}
    </span>
  );
}

/** A duration cell that renders a dash for "still going" rather than 0ms. */
export function Duration({ ms }: { ms: number | undefined | null }): JSX.Element {
  return <span className="num">{formatDuration(ms)}</span>;
}

export function Mono({ children, title }: { children: ReactNode; title?: string }): JSX.Element {
  return (
    <code className="mono" title={title}>
      {children}
    </code>
  );
}